/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import msdyn365Commerce, * as Msdyn365 from '@msdyn365-commerce/core';
import { Alert, IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import * as React from 'react';

import { BusinessPartner, BusinessPartnerUser, CatalogsDataActions, CommerceException } from '@msdyn365-commerce/retail-proxy';
import { validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import { IDlvryBusinessPartnersData } from './dlvry-business-partners.data';
import { IDlvryBusinessPartnersProps } from './dlvry-business-partners.props.autogenerated';
import { BusinessPartnerUsersTable } from './components/business-partner-users-table';
import { BusinessPartnersTable } from './components/business-partners-table';
import { ICookieSetOptions } from '@msdyn365-commerce/core';

/**
 * Props for the module view.
 */
export interface IBusinessPartnersViewProps extends IDlvryBusinessPartnersProps<IDlvryBusinessPartnersData> {
    businessPartnersProps: IModuleProps;
    businessPartnersHeading?: React.ReactNode;
    businessPartnerUsersHeading?: React.ReactNode;
    businessPartnersTable?: React.ReactNode;
    businessPartnerUsersTable?: React.ReactNode;
    backToBusinessPartnersLink?: React.ReactNode;
    catalogLoadingIndicator?: React.ReactNode;
    catalogLoadingErrorMessage?: React.ReactNode;
    selectedBusinessPartner?: BusinessPartner;
    selectedBusinessPartnerUser?: BusinessPartnerUser;
    cookieConsentNotGivenErrorMessage?: React.ReactNode;
}

/**
 * State of BusinessPartners component.
 */
export interface IBusinessPartnersState {
    selectedBusinessPartner?: BusinessPartner;
    selectedBusinessPartnerUser?: BusinessPartnerUser;
    isLoadingCatalog?: boolean;
    loadCatalogErrorMessage?: string;
}

/**
 * The class name root for the module.
 */
export const moduleClassName: string = 'ms-business-partners';

/**
 * BusinessPartners component.
 */
class BusinessPartners extends React.Component<IDlvryBusinessPartnersProps<IDlvryBusinessPartnersData>, IBusinessPartnersState> {
    constructor(props: IDlvryBusinessPartnersProps<IDlvryBusinessPartnersData>) {
        super(props);
        this.state = {};
    }

    public componentDidMount() {
        this.props.context.request.cookies.removeAccountSelectionCookie();
    }

    public shouldComponentUpdate(nextProps: IDlvryBusinessPartnersProps<IDlvryBusinessPartnersData>, nextState: IBusinessPartnersState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const {
            config: { className, pageSize, businessPartnersHeading, businessPartnerUsersHeading }
        } = this.props;
        const viewProps: IBusinessPartnersViewProps = {
            ...this.props,
            selectedBusinessPartner: this.state.selectedBusinessPartner,
            selectedBusinessPartnerUser: this.state.selectedBusinessPartnerUser,
            businessPartnersProps: {
                moduleProps: this.props,
                className: classname(moduleClassName, className)
            },
            businessPartnersHeading: !this.state.selectedBusinessPartner && businessPartnersHeading && businessPartnersHeading.text && (
                <Msdyn365.Text
                    className={`${moduleClassName}__heading`}
                    tag={businessPartnersHeading.tag}
                    text={businessPartnersHeading.text}
                />
            ),
            businessPartnerUsersHeading: this.state.selectedBusinessPartner &&
                businessPartnerUsersHeading &&
                businessPartnerUsersHeading.text && (
                    <Msdyn365.Text
                        className={`${moduleClassName}__heading`}
                        tag={businessPartnerUsersHeading.tag}
                        text={businessPartnerUsersHeading.text}
                    />
                ),
            businessPartnersTable: !this.state.selectedBusinessPartner ? (
                <BusinessPartnersTable
                    {...this.props}
                    pageSize={pageSize}
                    className={`${moduleClassName}__table-list`}
                    onSelect={this.onSelectBusinessPartner}
                />
            ) : (
                undefined
            ),
            businessPartnerUsersTable: this.state.selectedBusinessPartner ? (
                <BusinessPartnerUsersTable
                    {...this.props}
                    pageSize={pageSize}
                    className={`${moduleClassName}__table-list`}
                    onSelect={this.onSelectBusinessPartnerUser}
                    businessPartner={this.state.selectedBusinessPartner}
                />
            ) : (
                undefined
            ),
            backToBusinessPartnersLink: this.state.selectedBusinessPartner ? (
                <a
                    className={`${moduleClassName}__back-link`}
                    role='button'
                    onClick={() => {
                        this.onSelectBusinessPartner();
                    }}
                >
                    {this.props.resources.backToBusinessPartnersLink}
                </a>
            ) : (
                undefined
            ),
            catalogLoadingIndicator: this.state.isLoadingCatalog ? (
                <span className={`${moduleClassName}__loading`}>{this.props.resources.loadingLabel}</span>
            ) : (
                undefined
            ),
            catalogLoadingErrorMessage: this.state.loadCatalogErrorMessage ? (
                <span className={`${moduleClassName}__alert`}>{this.state.loadCatalogErrorMessage}</span>
            ) : (
                undefined
            ),
            cookieConsentNotGivenErrorMessage: !this.props.context.request.cookies.isConsentGiven() ? (
                <Alert color='danger'>{this.props.resources.cookieConsentRequiredMessage}</Alert>
            ) : (
                undefined
            )
        };
        return this.props.renderView(viewProps);
    }

    /**
     * Event handler of business partner selection.
     * @param businessPartner - Selected business partner.
     */
    private readonly onSelectBusinessPartner = (businessPartner?: BusinessPartner) => {
        this.setState({
            selectedBusinessPartner: businessPartner
        });
    };

    /**
     * Event handler of business partner user selection.
     * @param businessPartnerUser - Selected business partner user.
     */
    private readonly onSelectBusinessPartnerUser = async (businessPartnerUser: BusinessPartnerUser) => {
        if (businessPartnerUser.AccountNumber) {
            this.setState({
                selectedBusinessPartnerUser: businessPartnerUser
            });

            // this.props.context.request.cookies.setAccountSelectionCookie(true, businessPartnerUser.AccountNumber);

            const options: ICookieSetOptions = {
                secure: true,
                sameSite: 'none'
            };
            (options as any).isEssential = true;
            this.props.context.request.cookies.set(msdyn365Commerce.accountSelectionCookie, `t:${businessPartnerUser.AccountNumber}`, options);

            const catalogId = await this.getCustomerDefaultCatalogId();
            validateCatalogId(catalogId);
            const homeUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext);
            if (homeUrl && Msdyn365.msdyn365Commerce.isBrowser && catalogId !== undefined) {
                const fullUrl = new URL(homeUrl, window.location.href);
                fullUrl.searchParams.set('catalogid', `${catalogId}`);
                window.location.href = fullUrl.href;
            } else {
                this.props.context.request.cookies.removeAccountSelectionCookie();
            }
        }
    };

    /**
     * Get the first catalog ID of the selected customer. Customer account number is passed in CAN header.
     */
    private readonly getCustomerDefaultCatalogId = async (): Promise<number | undefined> => {
        this.setState({
            isLoadingCatalog: true
        });

        const requestContext = this.props.context.request;
        const channelId = requestContext.apiSettings.channelId ? Number(requestContext.apiSettings.channelId) : 0;

        try {
            const productCatalogs = await CatalogsDataActions.getCatalogsAsync(
                { callerContext: this.props.context.actionContext },
                channelId,
                true
            );
            this.setState({
                isLoadingCatalog: false
            });
            return productCatalogs[0]?.RecordId || 0;
        } catch (e) {
            this.setState({
                isLoadingCatalog: false,
                loadCatalogErrorMessage:
                    (e as { data: CommerceException })?.data?.LocalizedMessage || this.props.resources.genericErrorMessage
            });
            return undefined;
        }
    };
}

export default BusinessPartners;
